import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";
import { CloseIcon } from "@/components/icons";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  showCloseButton?: boolean;
  /**
   * When true, the Modal wraps the body in `p-6` so body content aligns
   * horizontally with the always-padded header. Default false because the
   * legacy consumers (forms, custom layouts) add their own padding to the
   * `children` they pass. New code should opt in so header + body share
   * one consistent inset.
   */
  bodyPadding?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  className,
  header,
  footer,
  showCloseButton = true,
  bodyPadding = false,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-xs"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={cn(
          "relative bg-white rounded-xl shadow-xl max-h-[90dvh] flex flex-col overflow-hidden",
          "animate-in fade-in-0 zoom-in-95 duration-200",
          {
            "w-full max-w-sm": size === "sm",
            "w-full max-w-md": size === "md",
            "w-full max-w-2xl": size === "lg",
            "w-full max-w-4xl": size === "xl",
          },
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed. Close button is absolutely positioned so the
            header content owns the full padded width — without this the
            close button eats ~44px from the right edge in the flex row
            and any hairlines / right-aligned actions inside the header
            stop short of the right padding. The header content reserves
            `pr-10` so its right edge doesn't run under the X. */}
        {(title || header) && (
          <div className="relative p-6 border-b shrink-0">
            <div className="pr-10">
              {header ? header : (
                <h2 className="text-lg font-semibold">{title}</h2>
              )}
            </div>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="absolute top-5 right-5 p-1 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                aria-label="Close modal"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Content - Scrollable */}
        <div
          className={cn(
            "flex-1 overflow-y-auto min-h-0",
            bodyPadding && "p-6",
          )}
        >
          {children}
        </div>

        {/* Footer - Fixed */}
        {footer && (
          <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
